/**
 * Bulk Update — the screen's copy, its operation list and its per-operation labels.
 *
 * Split out of useBulkOverride.js, which is the file the repo's max-lines rule keeps catching.
 * None of this is state: it is what each operation is called, what it warns about, and which
 * operations are shippable at all.
 */

import { omit } from 'lodash';

import { prescriptionReviewStatus } from 'common/constants/prescriptionStatus';

import { bulkOverrideOperation } from 'api/requests';

export const BULK_OVERRIDE_OPERATION = bulkOverrideOperation;

/* NDC_UPDATE is the only operation with a backend: `BulkLineItemOverrideInput` carries
 * `ndcOverrides` and nothing else. NEEDS_REVIEW_UPDATE and PRODUCT_ID_UPDATE are enabled for
 * development against the mock, which implements `needsReviewOverrides` and
 * `productIdOverrides`; against a real API both are rejected at GraphQL validation until core
 * carries those arrays. */
const ALL_BULK_OVERRIDE_ACTIONS = [
  {
    operation: BULK_OVERRIDE_OPERATION.NDC_UPDATE,
    title: 'Pre-fulfillment NDC update',
    description: 'Change the NDC before the order is filled. The product stays as it is.',
    continueLabel: 'Continue to pre-fulfillment NDC update',
    icon: 'barcode',
    requiresDrug: true,
  },
  {
    operation: BULK_OVERRIDE_OPERATION.PRODUCT_ID_UPDATE,
    title: 'Bulk product change',
    description: 'Swap the product on the orders you select, then select its NDC.',
    continueLabel: 'Continue to bulk product change',
    icon: 'swap',
    requiresDrug: true,
  },
  {
    operation: BULK_OVERRIDE_OPERATION.NEEDS_REVIEW_UPDATE,
    title: 'Bulk move to review',
    description: 'Send the orders you select to a review queue with a reason.',
    continueLabel: 'Continue to bulk review',
    icon: 'flag',
    requiresDrug: false,
  },
  {
    operation: BULK_OVERRIDE_OPERATION.UNFULFILLMENT_UPDATE,
    title: 'Bulk unfulfillment',
    description: 'Return filled orders to product fulfillment before they ship.',
    continueLabel: 'Continue to bulk unfulfillment',
    icon: 'undo',
    requiresDrug: true,
  },
  {
    operation: BULK_OVERRIDE_OPERATION.QUANTITY_UPDATE,
    title: 'Bulk quantity update',
    description: 'Filter by a quantity and change it, before the orders are filled.',
    continueLabel: 'Continue to bulk quantity update',
    icon: 'barcode',
    requiresDrug: true,
  },
  {
    operation: BULK_OVERRIDE_OPERATION.FILL_LATER_SPLIT,
    title: 'Bulk fill later',
    description: 'Reduce the quantity and schedule the remainder as a fill later.',
    continueLabel: 'Continue to bulk fill later',
    icon: 'swap',
    requiresDrug: true,
  },
];

const SHIPPABLE_OPERATIONS = [
  BULK_OVERRIDE_OPERATION.NDC_UPDATE,
  BULK_OVERRIDE_OPERATION.PRODUCT_ID_UPDATE,
  BULK_OVERRIDE_OPERATION.NEEDS_REVIEW_UPDATE,
  BULK_OVERRIDE_OPERATION.UNFULFILLMENT_UPDATE,
  BULK_OVERRIDE_OPERATION.QUANTITY_UPDATE,
  BULK_OVERRIDE_OPERATION.FILL_LATER_SPLIT,
];

export const BULK_OVERRIDE_ACTIONS = ALL_BULK_OVERRIDE_ACTIONS.filter(({ operation }) =>
  SHIPPABLE_OPERATIONS.includes(operation)
);

export const BULK_OVERRIDE_REVIEW_REASONS = omit(prescriptionReviewStatus, [
  'OTHER_REVIEW',
  'ADD_ON_REVIEW',
  'PRESCRIPTION_DUPLICATE_REVIEW',
]);

export const BULK_OVERRIDE_STANDING_FILTER_LABEL = {
  [bulkOverrideOperation.NDC_UPDATE]: 'Unfilled orders only',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: 'Unfilled orders only',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]: 'Open orders not already in review',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: 'Filled orders awaiting verification only',
  [bulkOverrideOperation.QUANTITY_UPDATE]: 'Unfilled orders only',
  [bulkOverrideOperation.FILL_LATER_SPLIT]: 'Unfilled orders not already in review',
  [bulkOverrideOperation.FILL_LATER_MOVE]: 'Unfilled orders not already in review',
};

export const BULK_OVERRIDE_APPLY_NOTE = {
  [bulkOverrideOperation.NDC_UPDATE]:
    'Select the orders, then select the NDC to update. Apply all sets that NDC on every selected order at once — you can still change any single row on its own, before or after.',
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]:
    'Select the orders, then select the new drug and its NDC. Apply all moves every selected order at once — you can still change any single row on its own, before or after.',
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]:
    'Select the orders, then select a review reason. Apply all sets that reason on every selected order at once — you can still set any single row on its own, before or after.',
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]:
    'Select the orders to unfulfill. Apply all returns every selected order to product fulfillment at once — there is nothing to set on a row, the selection is the change.',
  [bulkOverrideOperation.QUANTITY_UPDATE]:
    'Filter by the quantity you want to change, then select the orders and enter the new one. Every row on screen shares one current quantity, so Apply all is a single before-and-after — you can still change any row on its own.',
  /* The wording carries more weight here than on the other five: nothing is ordered when this
   * commits. A fill_later_item row is written per order, and each new order is created later, on
   * the chosen date and only once that original has shipped. */
  /* Deliberately the shortest of the six. The schedule beside it grows a row at a time, so every
   * line spent here is a line the operator scrolls past to reach the fields. */
  [bulkOverrideOperation.FILL_LATER_SPLIT]:
    'Build the fill later schedule — a quantity and a date for each split — then select the orders and apply it to all of them. Each quantity becomes a scheduled fill later, not an order — every new order is created on its own date, once the original has shipped.',
  [bulkOverrideOperation.FILL_LATER_MOVE]:
    'Pick the fill later date, then select the orders and apply it to all of them. The whole item leaves its order — there is no quantity to set and nothing is left behind to fill now.',
};

export const findBulkOverrideAction = (operation) =>
  BULK_OVERRIDE_ACTIONS.find((action) => action.operation === operation);

export const BULK_OVERRIDE_HOWTO = {
  [bulkOverrideOperation.NDC_UPDATE]: {
    steps: [
      [
        'Select a drug',
        'Only orders currently using that drug can be changed. Every open order for that drug is listed — across all facilities and partners.',
      ],
      [
        'Select orders, then the new NDC',
        'Set one NDC for the entire selection, or change any individual row on its own. Nothing is written yet.',
      ],
      [
        'Review the summary, then confirm',
        'The summary shows how many orders will change and the NDC each one will be changed to. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'high',
      title: 'Before you confirm',
      items: [
        'Only orders that are **not yet filled** appear on this screen. Once an order is filled, its NDC cannot be changed here.',
        'This updates **every selected order** in one action, across facilities and partners.',
        '**There is no bulk undo.** To reverse the change, you must create another override for each affected order.',
      ],
    },
  },
  [bulkOverrideOperation.PRODUCT_ID_UPDATE]: {
    steps: [
      [
        'Select the drug to move off',
        'Only orders currently using that drug can be moved. Every open order for that drug is listed.',
      ],
      [
        'Select orders, then the replacement',
        'Choose the new product, then its NDC. One choice for the entire selection, or row by row.',
      ],
      [
        'Review the summary, then confirm',
        'The summary names both products and shows how many orders will change. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'high',
      title: 'This changes what the patient receives',
      items: [
        'Every order you selected moves to a **different product and a different NDC**.',
        '**There is no bulk undo.** To reverse the change, you must create another product change for each affected order.',
      ],
    },
  },
  [bulkOverrideOperation.NEEDS_REVIEW_UPDATE]: {
    steps: [
      [
        'Select a drug',
        'Only orders for that drug are listed. Orders that are already in review, or that have moved past verification, will not appear here.',
      ],
      [
        'Filter, select orders, then select a reason',
        'Filter the orders, select the ones you want to move to review, and choose a reason from the standard review list. You can apply one reason to the entire selection or choose a reason row by row.',
      ],
      [
        'Review the summary, then confirm',
        'The summary shows how many orders will be moved to review and lists every reason you selected. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'low',
      title: 'What this does',
      items: [
        'The selected orders **leave the bulk queue and will not ship** until the review is cleared.',
        'The reason you select is visible to whoever clears the review, so make sure it is the most accurate one.',
        '**This action is reversible.** Clearing the review returns the order to the queue.',
      ],
    },
  },
  [bulkOverrideOperation.FILL_LATER_SPLIT]: {
    steps: [
      [
        'Choose a fill later type',
        '**Split item by quantity** keeps part of the quantity on the order and schedules the remainder. **Move items** takes the whole item to a fill later date and leaves nothing on the order. You choose on the next step.',
      ],
      [
        'Select a drug and a date, then the orders',
        'Only orders for that drug are listed, and only the ones not yet filled and not already in review. The date you pick is when the fill later is due, and it applies to the whole selection. A split also asks how much to take off each order; a move does not. Nothing is written yet.',
      ],
      [
        'Review the summary, then confirm',
        'The summary shows how many orders are affected and what each one schedules — and, for a split, what each original is left with. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'low',
      title: 'Nothing is ordered when you confirm',
      items: [
        'Whichever type you choose, the result is a **scheduled fill later**, not an order. The order is created on the date you picked.',
        'A **split** leaves the original order with the rest of the quantity, and it carries on through fulfillment as normal. Its new order waits until that original has shipped.',
        'A **move** takes the whole item — nothing is left behind to fill. When every item on an order moves, that order itself is held.',
        'An order that already has a scheduled fill later **can be split again.** Each split reduces the original further and schedules its own remainder.',
        'Scheduled fill laters are managed on the **Fill Later queue**, where they can be rescheduled or cancelled before their order is created.',
      ],
    },
  },
  [bulkOverrideOperation.FILL_LATER_MOVE]: {
    steps: [
      [
        'Select a drug and a date',
        'Only orders for that drug are listed, and only the ones not yet filled and not already in review. The date you pick is when the items should be filled, and it applies to the whole selection.',
      ],
      [
        'Select the orders to move',
        'There is nothing to set on a row: the whole item moves, so the orders you select are the items that become scheduled fill laters. Nothing is written yet.',
      ],
      [
        'Review the summary, then confirm',
        'The summary shows how many items will move and the date they are scheduled for. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'low',
      title: 'Nothing is ordered when you confirm',
      items: [
        'The **whole item** leaves its order. Unlike a split, nothing is left behind to fill now.',
        'Each item becomes a **scheduled fill later** and its order is held until the date you picked.',
        'When **every** item on an order moves, that order itself is held rather than a new one being created.',
        'Scheduled fill laters are managed on the **Fill Later queue**, where they can be rescheduled or cancelled before their order is created.',
      ],
    },
  },
  [bulkOverrideOperation.UNFULFILLMENT_UPDATE]: {
    steps: [
      [
        'Select a drug',
        'Only orders for that drug are listed, and only the ones already filled but not yet shipped — across all facilities and partners.',
      ],
      [
        'Select the orders to return',
        'There is nothing to set on a row: the orders you select are the orders that go back to product fulfillment. Nothing is written yet.',
      ],
      [
        'Review the summary, then confirm',
        'The summary shows how many orders will be returned to product fulfillment. This is your last chance to cancel.',
      ],
    ],
    warning: {
      level: 'high',
      title: 'This undoes work already done',
      items: [
        'Every selected order **returns to product fulfillment** and its order goes back to **OPEN**.',
        'The **lot reservations are released** — the stock those fills held goes back to inventory.',
        'This applies **across facilities and partners**, in a single action.',
        '**Re-filling means going through fulfillment again.** There is no bulk redo.',
      ],
    },
  },
};

export const BULK_OVERRIDE_IDLE_HOWTO = {
  title: 'Two steps, whichever you choose',
  body:
    'Select the action you want to perform here. On the next screen, you’ll select the orders, ' +
    'apply the change, review the summary, and confirm. Nothing is written until you confirm.',
};

export const BULK_OVERRIDE_IDLE_WARNING = {
  level: 'high',
  title: 'These are bulk actions',
  items: [
    'Each one applies to **every order you select**, in a single action.',
    '**There is no bulk undo** — reversing a change means applying another change to the ' +
      'affected orders.',
    'Select an action above to see exactly what it does before you commit.',
  ],
};

export const FILL_LATER_MODE = {
  SPLIT_QUANTITY: 'SPLIT_QUANTITY',
  MOVE_ITEMS: 'MOVE_ITEMS',
};

export const FILL_LATER_MODES = [
  {
    mode: FILL_LATER_MODE.SPLIT_QUANTITY,
    title: 'Split item by quantity',
    description:
      'Keep part of the quantity on the order and schedule the remainder as a fill later.',
    continueLabel: 'Continue to split by quantity',
    icon: 'barcode',
  },
  {
    mode: FILL_LATER_MODE.MOVE_ITEMS,
    title: 'Move items',
    description: 'Move the whole item to a fill later date. Nothing is left on the order.',
    continueLabel: 'Continue to move items',
    icon: 'swap',
  },
];

export const fillLaterModeFlags = (operation, mode) => {
  const isFillLater = operation === BULK_OVERRIDE_OPERATION.FILL_LATER_SPLIT;
  const isFillLaterMove = isFillLater && mode === FILL_LATER_MODE.MOVE_ITEMS;

  return { isFillLaterMove, isFillLaterSplit: isFillLater && !isFillLaterMove };
};
